/* Shipping-calculator v2  –  “Quote for cart” toggle ✚ scenario matrix
   ▸ Default (toggle OFF)  →  quote ONE unit of the viewing product
   ▸ Toggle ON (“Quote for cart”)
       - If cart empty          → show “Nothing is in your cart.”
       - If cart has items      → quote cart items ONLY (does NOT add PDP product)
*/
if (!customElements.get('shipping-calculator')) {
  class ShippingCalculator extends HTMLElement {
    constructor () {
      super();

      /* ELEMENT HOOKS */
      this.zip        = this.querySelector('.js-zip-input');
      this.country    = this.querySelector('.js-country-select');
      this.province   = this.querySelector('.js-province-select');
      this.cartToggle = this.querySelector('.js-cart-only-toggle');   // ← new
      this.button     = this.querySelector('button');
      this.ratesBox   = this.querySelector('.js-rates');
      this.errorsBox  = this.querySelector('.js-errors');

      /* LISTENERS */
      this.zip.addEventListener('keydown', e => {
        if (e.key === 'Enter') { e.preventDefault(); this.handleSubmit(); }
      });
      this.button.addEventListener('click', () => this.handleSubmit());
    }

    /* ───────────────── helpers ───────────────── */

    get variantId () {
      return document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
    }

    async getCart () {
      return fetch('/cart.js').then(r => r.json());
    }

    async clearCart () {
      await fetch('/cart/clear.js', { method:'POST' });
    }

    async addLine (id, qty = 1) {
      return fetch('/cart/add.js', {
        method : 'POST',
        headers: { 'Content-Type':'application/json' },
        body   : JSON.stringify({ items:[{ id, quantity: qty }] })
      }).then(r => r.json());
    }

    async addLines (items) {
      if (!items.length) return;
      await fetch('/cart/add.js', {
        method : 'POST',
        headers: { 'Content-Type':'application/json' },
        body   : JSON.stringify({ items })
      });
    }

    async removeLineByKey (key) {
      await fetch('/cart/change.js', {
        method : 'POST',
        headers: { 'Content-Type':'application/json' },
        body   : JSON.stringify({ id: key, quantity: 0 })
      });
    }

    formatMoney (cents) {
      /* theme.settings.moneyWithCurrencyFormat contains "{{ amount }}" token */
      return theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, cents);
    }

    /* ───────────────── main flow ───────────────── */

    async handleSubmit () {
      /* UI reset */
      this.errorsBox.hidden = true;
      this.ratesBox.hidden  = true;
      this.button.disabled  = true;
      this.button.classList.add('is-loading');

      const qs = new URLSearchParams({
        'shipping_address[zip]'    : this.zip.value.trim(),
        'shipping_address[country]': this.country.value,
        'shipping_address[province]': this.province.value
      }).toString();

      const quoteForCart = this.cartToggle?.checked;  // true = cart, false = product-only
      const cartSnapshot = await this.getCart();
      const cartHasItems = cartSnapshot.item_count > 0;

      /* QUICK guard for scenario 2 – cart empty + toggle ON */
      if (quoteForCart && !cartHasItems) {
        this.showErrors({ notice: 'Nothing is in your cart.' });
        this.finish();   // restore UI
        return;
      }

      /* VARIABLES for later cleanup */
      let backupItems = [], tempKey = null;

      try {
        if (quoteForCart) {
          /* ── Quote CART ONLY ──
             – No cart manipulation needed
             – PDP variant NOT auto-added
          */
        } else {
          /* ── Quote PRODUCT ONLY ── */

          if (!this.variantId) throw new Error('Variant ID not found on product form.');

          if (cartHasItems) {
            /* backup + clear */
            backupItems = cartSnapshot.items.map(i => ({
              id:        i.id,
              quantity:  i.quantity,
              properties:i.properties || undefined
            }));
            await this.clearCart();
          }

          /* add 1 × product */
          const addRes = await this.addLine(this.variantId, 1);
          tempKey = addRes.items?.[0]?.key || null;
        }

        /* ── Fetch ShipNerd / Shopify rates ── */
        const resp = await fetch(`${theme.routes.cart}/shipping_rates.json?${qs}`);
        const data = await resp.json();

        /* ── Restore cart state if we changed it ── */
        if (!quoteForCart) {
          await this.clearCart();
          if (backupItems.length) await this.addLines(backupItems);
        }

        /* Show result */
        resp.ok ? this.showRates(data) : this.showErrors(data);

      } catch (err) {
        console.error('[shipping-calculator]', err);
        this.showErrors({ error: 'Could not fetch rates. Please try again.' });
      }

      /* cleanup temp line-item if something went sideways */
      if (tempKey && quoteForCart === false) {
        await this.removeLineByKey(tempKey);
      }

      this.finish();
    }

    /* ───────────────── UI helpers ───────────────── */

    finish () {
      this.button.classList.remove('is-loading');
      this.button.disabled = false;
    }

    showRates (data) {
      const heading = this.ratesBox.querySelector('.js-rates-heading');
      const body    = this.ratesBox.querySelector('.js-rates-text');
      body.innerHTML = '';

      if (data.shipping_rates?.length) {
        const k = data.shipping_rates.length === 1 ? 'singleRate' : 'multipleRates';
        heading.hidden = false;
        heading.textContent = theme.strings.shippingCalculator[k];

        body.innerHTML = `<ul class="styled-list">${
          data.shipping_rates.map(r =>
            `<li>${r.name}: ${this.formatMoney(r.price)}</li>`).join('')
        }</ul>`;
      } else {
        heading.hidden = true;
        body.textContent = theme.strings.shippingCalculator.noRates;
      }
      this.ratesBox.hidden = false;
    }

    showErrors (obj) {
      const wrap = this.errorsBox.querySelector('.js-errors-text');
      wrap.innerHTML = `<ul class="styled-list">${
        Object.values(obj).map(v => `<li>${v}</li>`).join('')
      }</ul>`;
      this.errorsBox.hidden = false;
    }
  }

  customElements.define('shipping-calculator', ShippingCalculator);
}
